import React, { useState } from 'react';
import { X } from 'lucide-react';
import { POSProduct, POSVariant } from '../../pages/pos/POSTerminal';

interface Props {
  product: POSProduct;
  onSelect: (product: POSProduct, variant: POSVariant, sizeName: string, stock: number) => void;
  onClose: () => void;
}

const POSVariantModal: React.FC<Props> = ({ product, onSelect, onClose }) => {
  const [selectedVariant, setSelectedVariant] = useState<POSVariant | null>(
    product.variants.length > 0 ? product.variants[0] : null
  );

  const activeVariants = product.variants.filter(v => v.is_active);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-white">{product.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Color Selection */}
        <div className="mb-4">
          <p className="text-xs text-gray-400 mb-2">Color</p>
          <div className="flex flex-wrap gap-2">
            {activeVariants.map((variant) => (
              <button
                key={variant.id}
                onClick={() => setSelectedVariant(variant)}
                className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition ${
                  selectedVariant?.id === variant.id
                    ? 'bg-blue-600 ring-2 ring-blue-400 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {variant.color_code && (
                  <span
                    className="w-4 h-4 rounded-full border border-gray-500"
                    style={{ backgroundColor: variant.color_code }}
                  />
                )}
                {variant.color}
              </button>
            ))}
          </div>
        </div>

        {/* Size Selection */}
        {selectedVariant && (
          <div>
            <p className="text-xs text-gray-400 mb-2">Size & Stock</p>
            <div className="grid grid-cols-2 gap-2">
              {selectedVariant.sizes.map((size) => (
                <button
                  key={size.name}
                  onClick={() => {
                    if (size.stock_quantity > 0) {
                      onSelect(product, selectedVariant, size.name, size.stock_quantity);
                    }
                  }}
                  disabled={size.stock_quantity <= 0}
                  className={`p-3 rounded-lg text-left transition ${
                    size.stock_quantity > 0
                      ? 'bg-gray-700 hover:bg-gray-600 cursor-pointer'
                      : 'bg-gray-700 opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">{size.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      size.stock_quantity > 0 ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                    }`}>
                      {size.stock_quantity} in stock
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {(size.price || selectedVariant.price || product.price).toLocaleString()} MMK
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default POSVariantModal;
